import React, { createContext, useContext, useState, useEffect } from "react";
import api from "../utils/api";

const SeasonContext = createContext();

export const useSeasonContext = () => {
  const context = useContext(SeasonContext);
  if (!context) {
    throw new Error("useSeasonContext must be used within SeasonProvider");
  }
  return context;
};

const CACHE_KEY = "seasons_cache";
const CACHE_TTL = 30 * 60 * 1000; // 30 minutes

const applySeasonTheme = (season) => {
  const root = document.documentElement;

  if (season?.themeBackground) {
    root.style.setProperty("--background-color", season.themeBackground);
  } else {
    root.style.removeProperty("--background-color");
  }

  if (season?.themeText) {
    root.style.setProperty("--text-color", season.themeText);
  } else {
    root.style.removeProperty("--text-color");
  }
};

export const SeasonProvider = ({ children, isAuthenticated }) => {
  const [currentSeason, setCurrentSeason] = useState(null);
  const [seasons, setSeasons] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }

    const fetchSeasons = async () => {
      try {
        // Try cache first
        let data = null;
        try {
          const cached = JSON.parse(
            sessionStorage.getItem(CACHE_KEY) || "null",
          );
          if (cached && Date.now() - cached.ts < CACHE_TTL) {
            data = cached.data;
          }
        } catch {
          // Ignore parse errors — treat as cache miss
        }

        if (!data) {
          const response = await api.get("/seasons");
          data = response.data;
          try {
            sessionStorage.setItem(
              CACHE_KEY,
              JSON.stringify({ ts: Date.now(), data }),
            );
          } catch {
            // Ignore storage errors (e.g. private browsing quota)
          }
        }

        const fetchedSeasons = data.seasons || [];
        const activeSeason = data.activeSeason || "2025-26";

        setSeasons(fetchedSeasons);
        setCurrentSeason(activeSeason);

        // Apply theme for the initial active season
        const activeSeasonData = fetchedSeasons.find(
          (s) => s.id === activeSeason,
        );
        applySeasonTheme(activeSeasonData);
      } catch (error) {
        console.error("Failed to fetch seasons:", error);
        setCurrentSeason("2025-26");
      } finally {
        setLoading(false);
      }
    };

    fetchSeasons();
  }, [isAuthenticated]);

  // Apply theme whenever the user switches season
  const handleSetCurrentSeason = (seasonId) => {
    setCurrentSeason(seasonId);
    const seasonData = seasons.find((s) => s.id === seasonId);
    applySeasonTheme(seasonData);
  };

  const value = {
    currentSeason,
    setCurrentSeason: handleSetCurrentSeason,
    seasons,
    loading,
  };

  return (
    <SeasonContext.Provider value={value}>{children}</SeasonContext.Provider>
  );
};
